import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, QrCode, CreditCard, Check, X, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api, useAuth } from '../App';

const MyBookingsPage = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await api.get('/bookings');
        setBookings(response.data);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const getStatusBadge = (status, paymentStatus) => {
    if (status === 'completed') {
      return <span className="badge-completed px-2.5 py-1 rounded-full text-xs font-medium">Completed</span>;
    }
    if (status === 'cancelled') {
      return <span className="badge-cancelled px-2.5 py-1 rounded-full text-xs font-medium">Cancelled</span>;
    }
    if (status === 'confirmed' || paymentStatus === 'deposit_paid' || paymentStatus === 'fully_paid') {
      return <span className="badge-confirmed px-2.5 py-1 rounded-full text-xs font-medium">Confirmed</span>;
    }
    return <span className="badge-pending px-2.5 py-1 rounded-full text-xs font-medium">Pending</span>;
  };

  const upcomingBookings = bookings.filter(b => 
    ['pending', 'confirmed'].includes(b.status) && new Date(b.date) >= new Date()
  );
  const pastBookings = bookings.filter(b => 
    b.status === 'completed' || new Date(b.date) < new Date()
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="p-2 hover:bg-muted rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-2xl font-bold">My Bookings</h1>
              <p className="text-muted-foreground text-sm">Manage your adventures</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-serif text-xl font-bold mb-2">No Bookings Yet</h2>
            <p className="text-muted-foreground mb-6">Start planning your next adventure!</p>
            <Link to="/tours">
              <Button className="btn-primary" data-testid="browse-tours-btn">Browse Tour</Button>
            </Link>
          </div>
        ) : (
          <Tabs defaultValue="upcoming" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 max-w-xs">
              <TabsTrigger value="upcoming" data-testid="upcoming-tab">
                Upcoming ({upcomingBookings.length})
              </TabsTrigger>
              <TabsTrigger value="past" data-testid="past-tab">
                Past ({pastBookings.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="space-y-4">
              {upcomingBookings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No upcoming bookings
                </div>
              ) : (
                upcomingBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} getStatusBadge={getStatusBadge} />
                ))
              )}
            </TabsContent>

            <TabsContent value="past" className="space-y-4">
              {pastBookings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No past bookings
                </div>
              ) : (
                pastBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} getStatusBadge={getStatusBadge} isPast />
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

const BookingCard = ({ booking, getStatusBadge, isPast = false }) => {
  const needsPayment = booking.payment_status === 'pending' && !isPast;

  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid={`booking-${booking.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-serif font-bold text-lg">{booking.tour_title}</h3>
            <p className="text-sm text-muted-foreground">
              Ref: {booking.id.slice(0, 8).toUpperCase()}
            </p>
          </div>
          {getStatusBadge(booking.status, booking.payment_status)}
        </div>

        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {booking.date}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {booking.time_slot}
          </span>
          <span>
            {booking.adults} Adult{booking.adults !== 1 ? 's' : ''}
            {booking.children > 0 && `, ${booking.children} Child${booking.children !== 1 ? 'ren' : ''}`}
          </span>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="text-sm">
            <span className="text-muted-foreground">Total: </span>
            <span className="font-bold">${booking.total_amount.toFixed(2)}</span>
            {booking.payment_status === 'fully_paid' && (
              <span className="text-green-600 ml-2 flex items-center gap-1 inline-flex">
                <Check className="w-3 h-3" /> Paid
              </span>
            )}
          </div>

          <div className="flex gap-2">
            {needsPayment && (
              <Link to={`/checkout/${booking.id}`}>
                <Button size="sm" className="btn-primary" data-testid={`pay-now-${booking.id}`}>
                  <CreditCard className="w-4 h-4 mr-1" />
                  Pay Now
                </Button>
              </Link>
            )}
            <Link to={`/confirmation/${booking.id}`}>
              <Button size="sm" variant="ghost" data-testid={`view-booking-${booking.id}`}>
                View
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyBookingsPage;
